import get from "lodash/get";
import map from "lodash/map";
import partial from "lodash/partial";
import compact from "lodash/compact";
import max from "lodash/max";
import every from "lodash/every";
import sum from "lodash/sum";
import round from "lodash/round";
import merge from "lodash/merge";
import startsWith from "lodash/startsWith";
import includes from "lodash/includes";
import size from "lodash/size";
import getWilks from "../utils/getWilks";
import i18n from "../i18n";

const Competition = (app) => {
  app
    // Create or update an competition.
    .post(
      "/competition/save/:competition?",
      async ({ req, res, assert, flash, locals, api, load, momentTZ }) => {
        const { body, params } = req;
        await load("editCompetition", { competition: params.competition });
        const { editCompetition, activeUser } = locals;
        const _id = get(editCompetition, "_id");
        const isNew = !_id;
        const isFuture = body.tense === "future";
        body.user = activeUser._id;
        delete body.tense;

        assert(body.date, 400, i18n.t("dashboard:competitionActions.specifyDay"));
        assert(body.name, 400, i18n.t("dashboard:competitionActions.specifyName"));
        assert(
          body.federation,
          400,
          i18n.t("dashboard:competitionActions.specifyFederation")
        );
        assert(
          body.level,
          400,
          i18n.t("dashboard:competitionActions.specifyLevel")
        );
        assert(
          body.category,
          400,
          i18n.t("dashboard:competitionActions.specifyCategory")
        );
        assert(body.type, 400, i18n.t("dashboard:competitionActions.specifyType"));
        assert(
          body.weightClass,
          400,
          i18n.t("dashboard:competitionActions.specifyWeightClass")
        );

        if (!isFuture) {
          assert(
            body.weight,
            400,
            i18n.t("dashboard:competitionActions.specifyBodyWeight")
          );
          assert(
            body.weight > 0,
            400,
            i18n.t("dashboard:competitionActions.bodyWeightGreaterThanZero")
          );
          assert(
            body.attempts,
            400,
            i18n.t("dashboard:competitionActions.fillOutAttempts")
          );
          assert(
            size(compact(body.attempts.bench)) >= 1,
            400,
            i18n.t("dashboard:competitionActions.fillOutBenchAttempts")
          );

          if (includes(body.type, "Bench Only")) {
            delete body.attempts.squat;
            delete body.attempts.deadlift;
          } else {
            assert(
              size(compact(body.attempts.squat)) >= 1,
              400,
              i18n.t("dashboard:competitionActions.fillOutSquatAttempts")
            );
            assert(
              size(compact(body.attempts.deadlift)) >= 1,
              400,
              i18n.t("dashboard:competitionActions.fillOutDeadliftAttempts")
            );
          }

          // Automatically calculate total.
          const bestAttempts = _(body.attempts)
            .mapValues(partial(map, _, Number))
            .mapValues(compact)
            .map(max)
            .value();

          body.total = every(bestAttempts, (attempt) => attempt > 0)
            ? sum(bestAttempts)
            : 0;

          if (body.calculate) {
            body.wilks = round(
              getWilks(activeUser.gender, body.weight, body.total),
              1
            );
            merge(editCompetition, body);

            return res.refreshWith(
              {
                wilks: body.wilks,
                total: body.total,
              },
              { url: "back" }
            );
          }

          assert(
            body.wilks,
            400,
            i18n.t("dashboard:competitionActions.specifyWilksOrCalculate")
          );
        }

        if (isNew) {
          await api("competition", { method: "POST", body });
        } else {
          await api(`competition/${_id}`, { method: "PUT", body });
        }
        flash(
          "success",
          isNew ? i18n.t("dashboard:competitionActions.successfullyAdded") : i18n.t("dashboard:competitionActions.successfullyUpdated")
        );
        res.redirect(
          `/dashboard/competitions/?list=${isFuture ? "upcoming" : "previous"} `
        );
      }
    )
    // Cancel editing an competition and remove the edited item from the cache.
    .get("/competition/cancel", async ({ req, res, session }) => {
      const { query } = req;
      session.items.forEach(({ key }) => {
        if (startsWith(key, "editCompetition")) session.delete(key);
      });
      res.redirect(query.redirect);
    })
    // Remove an competition
    .get(
      "/competition/remove/:competition",
      async ({ req, res, assert, flash, locals, api, load, momentTZ }) => {
        if (
          process.browser &&
          !confirm(i18n.t("dashboard:competitionActions.confirmDelete"))
        ) {
          return res.backDontScroll();
        }

        const { body, params } = req;
        await load("editCompetition", { competition: params.competition });
        const { editCompetition } = locals;
        assert(editCompetition, 400, i18n.t("dashboard:competitionActions.alreadyRemoved"));

        const isFuture = momentTZ(editCompetition.date).isAfter(
          momentTZ().endOf("day")
        );
        await api(`competition/${editCompetition._id}`, { method: "DELETE" });
        flash("success", i18n.t("dashboard:competitionActions.successfullyRemoved"));
        res.redirect(
          `/dashboard/competitions/?list=${isFuture ? "upcoming" : "previous"}`
        );
      }
    );
};

export default Competition;
